"use client";

import { ArrowRight, Calendar, Check, Stethoscope, Video } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tabs = [{
  value: "respite",
  label: "Respite Care",
  title: "Restorative care for your family.",
  description: "We provide compassionate, reliable respite care that gives caregivers the space to recharge while ensuring your loved ones are in a safe, engaging, and supportive environment.",
  services: ["Short-term relief sessions", "Trained support staff", "Safe, inclusive environments", "Personalized care plans", "Flexible scheduling", "Caregiver peace of mind"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/respite_care_support.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/respite_care_support.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/respite_care_support.png-wvc-sizes",
    alt: "Caregiver supporting a child in a warm environment"
  },
  stat: "95%",
  statLabel: "of families feel more supported after our sessions"
}, {
  value: "advocacy",
  label: "Advocacy",
  title: "Your voice, amplified.",
  description: "Navigating systems can be overwhelming. We stand with you, providing the tools, knowledge, and direct support needed to ensure your family receives the resources and respect you deserve.",
  services: ["Educational system guidance", "Resource navigation", "Policy support", "Empowerment workshops", "Community representation", "Rights education"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/advocacy_support_services.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/advocacy_support_services.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/advocacy_support_services.png-wvc-sizes",
    alt: "Advocacy meeting with community partners"
  },
  stat: "100+",
  statLabel: "families empowered through our advocacy"
}, {
  value: "inclusive",
  label: "Inclusive Programs",
  title: "Belonging for everyone.",
  description: "Our programs are designed to celebrate neurodiversity. From sensory-friendly events to social clubs, we create spaces where every child and family member can thrive exactly as they are.",
  services: ["Sensory-friendly activities", "Social skill building", "Inclusive arts & crafts", "Peer connection groups", "Family-centered events", "Community outings"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/inclusive_programs_community.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/inclusive_programs_community.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/inclusive_programs_community.png-wvc-sizes",
    alt: "Children participating in an inclusive activity"
  },
  stat: "All",
  statLabel: "abilities and neurotypes are celebrated here"
}, {
  value: "bilingual",
  label: "Bilingual Resources",
  title: "Bridging language gaps.",
  description: "We believe access to support shouldn't be limited by language. Our bilingual resources ensure that Spanish-speaking families have the same access to advocacy, care, and community connection.",
  services: ["Bilingual resource guides", "Spanish-language advocacy", "Translation assistance", "Culturally responsive care", "Bilingual support groups", "Community outreach"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/bilingual_outreach_support.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/bilingual_outreach_support.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/bilingual_outreach_support.png-wvc-sizes",
    alt: "Bilingual staff assisting a family"
  },
  stat: "Fluent",
  statLabel: "support for our diverse community"
}, {
  value: "partnerships",
  label: "Partnerships",
  title: "Stronger together.",
  description: "We build bridges between families, local businesses, and service providers. By working together, we create a more accessible, understanding, and vibrant neighborhood for everyone.",
  services: ["Community business alliances", "Service provider networking", "Collaborative events", "Shared resource pools", "Neighborhood advocacy", "Unified support systems"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/community_partnerships_network.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/community_partnerships_network.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700933/img/community_partnerships_network.png-wvc-sizes",
    alt: "Community partners working together"
  },
  stat: "50+",
  statLabel: "local partners committed to inclusion"
}];
const highlights = [{
  icon: Calendar,
  title: "Flexible Scheduling",
  description: "We offer adaptable support options to fit the unique rhythm of your family life."
}, {
  icon: Video,
  title: "Virtual Advocacy",
  description: "Connect with our advocates from home for guidance, planning, and support."
}, {
  icon: Stethoscope,
  title: "Care Coordination",
  description: "We act as your partner in navigating complex systems and connecting with resources."
}];
export default function ComprehensiveCareSupport() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: tabsRef,
    isVisible: tabsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: highlightsRef,
    isVisible: highlightsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="691" id="comprehensive-care-support" className="bg-background py-20 md:py-28 lg:py-32 text-foreground">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`max-w-3xl transition-all duration-700 ease-out ${headerVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <div className="flex items-center gap-3">
            <span className="h-px w-10 bg-primary/60" />
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-secondary">
              Our Mission
            </span>
          </div>
          <h2 className="mt-6 font-default text-4xl font-semibold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-[64px]">
            Comprehensive support for neurodiverse families.
          </h2>
          <p className="mt-6 max-w-2xl font-default text-base leading-relaxed text-muted-foreground sm:text-lg">
            At Hope Torchlighters, we provide respite care, advocacy, and inclusive programs designed to empower your family. We are here to walk alongside you, ensuring you have the resources and community you need to thrive.
          </p>
        </div>

        {}
        <div ref={tabsRef} className={`mt-16 transition-all duration-700 ease-out ${tabsVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <Tabs defaultValue="respite" className="w-full">
            {}
            <div className="border-b border-border">
              <TabsList className="flex h-auto w-full justify-start gap-2 overflow-x-auto bg-transparent p-0 md:gap-8">
                {tabs.map((tab, i) => <TabsTrigger key={tab.value} data-index={i} value={tab.value} className="relative h-auto flex-none whitespace-nowrap border-0 border-b-2 border-transparent bg-transparent px-1 py-4 font-default text-base font-medium text-muted-foreground transition-colors duration-300 hover:text-primary data-[state=active]:border-secondary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none md:text-lg">
                    {tab.label}
                  </TabsTrigger>)}
              </TabsList>
            </div>

            {}
            {tabs.map((tab, i) => <TabsContent key={tab.value} data-index={i} value={tab.value} className="mt-12 animate-in fade-in slide-in-from-bottom-2 duration-500 md:mt-16">
                <div className="grid grid-cols-1 gap-10 lg:grid-cols-5 lg:gap-16">
                  {}
                  <div className="relative lg:col-span-2">
                    <div className="overflow-hidden rounded-lg bg-muted">
                      <img src={tab.image.src} data-wvc-srcset={tab.image.srcset} data-wvc-sizes={tab.image.sizes} alt={tab.image.alt} loading="lazy" decoding="async" className="aspect-[4/5] h-full w-full object-cover" />
                    </div>
                    {}
                    <Card className="absolute -bottom-6 -right-4 w-[200px] rounded-lg border border-border/60 bg-card shadow-lg md:-right-8 md:w-[220px]">
                      <CardContent className="px-5 py-5">
                        <div className="font-default text-3xl font-semibold text-secondary md:text-4xl">
                          {tab.stat}
                        </div>
                        <p className="mt-2 font-default text-xs leading-relaxed text-muted-foreground">
                          {tab.statLabel}
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {}
                  <div className="lg:col-span-3 lg:pl-4">
                    <h3 className="font-default text-2xl font-semibold leading-tight text-foreground sm:text-3xl">
                      {tab.title}
                    </h3>
                    <p className="mt-5 font-default text-base leading-relaxed text-muted-foreground">
                      {tab.description}
                    </p>

                    <ul className="mt-8 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                      {tab.services.map((service, j) => <li key={service} data-index={j} className="flex items-start gap-3">
                          <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-md bg-secondary/10">
                            <Check className="h-3 w-3 text-secondary" strokeWidth={2.5} />
                          </span>
                          <span className="font-default text-sm text-foreground">
                            {service}
                          </span>
                        </li>)}
                    </ul>

                    <div className="mt-10">
                      <Button asChild variant="link" className="group h-auto p-0 font-default text-sm font-bold text-secondary hover:no-underline">
                        <Link to="/ConnectPage">
                          Get in touch
                          <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>)}
          </Tabs>
        </div>

        {}
        <Separator className="mt-20 bg-primary/30 md:mt-28" />

        {}
        <div ref={highlightsRef} className={`mt-12 grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8 transition-all duration-700 ease-out ${highlightsVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          {highlights.map((item, i) => {
          const Icon = item.icon;
          return <Card key={item.title} data-index={i} className="border border-border/60 bg-card shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg rounded-lg">
                <CardContent className="flex items-start gap-4 px-6 py-6">
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
                  </span>
                  <div>
                    <h4 className="font-default text-lg font-semibold text-foreground">
                      {item.title}
                    </h4>
                    <p className="mt-1.5 font-default text-sm leading-relaxed text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}